import get from 'lodash.get';
import { READY_GATE } from '@/services/bootstrap';

export const metrics = {
  responseEndTime: 'metric1',
  domLoadTime: 'metric2',
  windowLoadTime: 'metric3',
  ttfb: 'metric4',
  stalled: 'metric5',
  download: 'metric6',
  startDuration: 'metric7',
  endDuration: 'metric8',
  cachedResources: 'metric9',
};

export const dimensions = {
  uid: 'dimension1',
  orgId: 'dimension2',
  hpInternal: 'dimension3',
  roles: 'dimension4',
  orgType: 'dimension5',
  selfProvisioned: 'dimension6',
  entityType: 'dimension7',
  windowId: 'dimension8',
  hitId: 'dimension9',
  hitTime: 'dimension10',
  mobileDeviceId: 'dimension11',
  appBuContext: 'dimension12',
  visitingOrgId: 'dimension13',
  bu:'dimension14',
  hpOrgType: 'dimension15',
  aaaCountry: 'dimension16',
  personas: 'dimension17',
};
export const ORPHANED_USER_ORG = 'ORPHAN_USER';

window.dataLayer = window.dataLayer || [];
function gtag(){window.dataLayer.push(arguments)}

export default function googleAnalyticsFactory(services) {
  const PAGE_VISIBILITY_LABEL = 'page-visibility';
  const PAGE_VISIBILITY_START_LABEL = `${PAGE_VISIBILITY_LABEL}-start`;
  const PAGE_VISIBILITY_END_LABEL = `${PAGE_VISIBILITY_LABEL}-end`;
  let visibilityPage;
  let analyticsKey;
  let analytics4Key;
  let otherKeys = [];
  let gaSearchCount = 0;
  let ga4EventFields = {};
  let historyChanges = true;
  let gazelleChanges = false;
  let manualChanges = false;
  let initialized = false;
  let firstPage = null;

  const thisService = {
    init,
    addTrackingId,
    setEnable,
    set,
    send,
    sendToTag,
    sendLegacy,
    gazelleSend,
    gazellePageTrack,
    eventTrack,
    exceptionTrack,
    pageTrack,
    setDebugMode,
    unitTest: {
      handleVisibilityChange,
      handleWindowClose,
      handlePageView,
    },
  };
  activate();
  return thisService;

  ///////////////

  function activate() {
    gtag('js', new Date());

    // At this point we can declare ga loaded
    services.bootstrap.readyGateCleared(READY_GATE.ga);

    // configureVueErrorReporting();
    registerForVisibilityEvents();
    services.window.addEventListener('popstate', handleHistoryChange);
  }

  function checkIfAnalyticsLoaded() {
    if (gtag) {
      initWindowGA();
    }
    else {
      if (gaSearchCount < 20) {
        gaSearchCount++;
        setTimeout(checkIfAnalyticsLoaded, 500);
      }
    }
  }

  function init(enable = true) {
    analytics4Key = services.session.getRegistryValue('analytics4Key');

    setEnable(enable);

    checkIfAnalyticsLoaded();
    addGtagScript();

    gtag('config', analytics4Key, {debug_mode: false, send_page_view: false});
    // console.log('GA4 Page tracking >> Initialized', analytics4Key);

    initGtag();

    // ga global function now correctly established
    initialized = true;
    sendNavigationTimingMetrics();
    sendApplicationLoadEvent();


    // if (firstPage) {
    //   _pageTrack(firstPage.id, firstPage.page, firstPage.source);
    // }
  }

  function setDebugMode(enable) {
    analytics4Key = services.session.getRegistryValue('analytics4Key');
    gtag('config', analytics4Key, { debug_mode: enable });
  }

  function handleHistoryChange(event) {
    if (!historyChanges) {
      return;
    }

    let page = window.location.pathname + (window.location.hash ? window.location.hash.split('?')[0] : '');

    //console.log('GA4 Page tracking >> Auto', event, page);
    if (!initialized) {
      firstPage = {
        id: null,
        page: page,
        source: 'Auto',
      };
      return;
    }

    _pageTrack(null, page, 'Auto');
  }

  function addTrackingId(gaKey, fields = {send_page_view: false}) {
    if (!otherKeys.includes(gaKey)) {
      otherKeys.push(gaKey);
    }

    gtag('config', gaKey, fields);
  }

  function initWindowGA() {
    const clientConfig = services.session.getClientConfig();
    let entityId = get(clientConfig, 'entity.id');
    let config = {
      'storage': 'none',
      'storeGac': false,
      'cookieDomain': 'auto',
    };
    if (entityId) {
      config.clientId = entityId;
    }

    gtag('consent', 'default', {
      'ad_storage': 'denied',
      //'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      //'analytics_storage': 'denied',
    });

    //services.window.ga('create', analyticsKey, config);
    //services.window.ga('set', 'allowAdPersonalizationSignals', false);
    //services.window.ga('set', 'allowAdFeatures', false);
    setGAFields();
    handleHistoryChange();
  }

  function setGA(name, value) {
    gtag('set', { name: value});
    //services.window.ga('set', name, value);
  }

  function setGAFields() {
    const clientConfig = services.session.getClientConfig();
    let locale = services.i18n.getNormalizedBcp47Locale();

    // Use beacon transport to minimize any impact on navigation and performance
    setGA('transport', 'beacon');

    let entityId = get(clientConfig, 'entity.id');
    if (entityId) {
      setGA('userId', entityId);
      setGA(dimensions.uid, entityId);
    }

    // entity type will be set by data import in the future
    let entityType = get(clientConfig, 'entity.type');
    if (entityType) {
      setGA(dimensions.entityType, entityType);
    }

    let managingContextId = get(clientConfig, 'managingContext.id');
    if (managingContextId) {
      setGA(dimensions.orgId, managingContextId);
    }

    let contextId = get(clientConfig, 'context.id');
    if (contextId) {
      if (managingContextId) {
        setGA(dimensions.visitingOrgId, contextId);
      } else {
        setGA(dimensions.orgId, contextId);
      }
    }
    else {
      if (entityType === 'User') {
        setGA(dimensions.orgId, ORPHANED_USER_ORG);
      }
    }

    let personas = get(clientConfig, 'entity.persona.myTypes', []);
    if (personas.length > 0) {
      setGA(dimensions.personas, personas.join('|'));
    }

    let currentAppId = services.app.getCurrentAppId();
    if (currentAppId) {
      setGA('title', currentAppId);
      setGA('appName', currentAppId);
      setGA('appId', currentAppId);
    }

    let experimentId = get(clientConfig, 'experiment.id');
    if (experimentId) {
      setGA('expId', experimentId);
    }

    let variantId = get(clientConfig, 'experiment.variant');
    if (variantId !== undefined) {
      setGA('expVar', variantId);
    }

    setGA('language', locale.toLowerCase());
  }

  function initGtag() {
    const clientConfig = services.session.getClientConfig();
    let locale = services.i18n.getNormalizedBcp47Locale();
    gtagRunFirst(() => {
      gtag('set', 'allowAdPersonalizationSignals', false);
      gtag('set', 'allowAdFeatures', false);
      setFields(clientConfig, locale);
    });
  }

  function setEnable(enable) {
    //console.log('GA Set Enable', enable);
    services.window[`ga-disable-${analytics4Key}`] = !enable;
    otherKeys.forEach(gaKey => {
      services.window[`ga-disable-${gaKey}`] = !enable;
    });
  }

  function gtagRunFirst(callback) {
    if (!gtag.q) {
      return callback();
    }

    let gaTempQueue = gtag.q;
    gtag.q = [];

    callback();

    gtag.q.push.apply(gtag.q, gaTempQueue);
  }

  // Adapted from google analytics tracking snippet: https://developers.google.com/analytics/devguides/collection/analyticsjs/tracking-snippet-reference
  function addGtagScript() {
    // Insert the script tag asynchronously.
    // Inserts above current tag to prevent blocking in addition to using the
    // async attribute.
    let gaScript = services.document.createElement('script');
    let currentScript = services.document.getElementsByTagName('script')[0];
    gaScript.async = 1;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${analytics4Key}`;
    currentScript.parentNode.insertBefore(gaScript, currentScript);
  }

  function setFields(clientConfig, locale) {
    ga4EventFields = {
      app_name: services.app.getCurrentApp()?.loc?.name,
      app_id: services.app.getCurrentAppId(),
      exp_id: get(clientConfig, 'experiment.id'),
      exp_var: get(clientConfig, 'experiment.variant'),
      send_to: analytics4Key,
    };
    // Use beacon transport to minimize any impact on navigation and performance
    set('transport', 'beacon');

    let entityId = get(clientConfig, 'entity.id');
    if (entityId) {
      // use built-in user_id, and custom dimension uid
      set('user_id', entityId);
      gtag('set', 'user_properties', {'uid': entityId});
    }

    let bu = get(clientConfig, 'context.bu', []);
    if (bu) {
      let myBu = bu.join(', ');
      set(dimensions.bu, myBu);
      ga4EventFields.bu = myBu;
    }

    // entity type will be set by data import in the future
    let entityType = get(clientConfig, 'entity.type');
    if (entityType) {
      gtag('set', 'user_properties', {'entity_type': entityType});
    }

    let orgType = get(clientConfig, 'context.type');
    if (orgType) {
      set('org_type', orgType);
      ga4EventFields.org_type = orgType;
      gtag('set', 'user_properties', {'org_type': orgType});
    }

    let roles = get(clientConfig, 'entity.roles');
    if (roles) {
      let roleNames = [];
      roles.forEach((role) => {
        roleNames.push(role.systemroleName);
      });
      gtag('set', 'user_properties', {'roles': roleNames.join(', ')});
    }

    let hpInternal = get(services.session.getOrg(), 'hpInternal', false);
    gtag('set', 'user_properties', {'hp_internal': hpInternal ? 'true' : 'false'});

    let personas = get(clientConfig, 'entity.persona.myTypes', []);
    if (personas.length > 0) {
      gtag('set', 'user_properties', {'personas': personas.join(', ')});
    }

    let currentAppId = services.app.getCurrentAppId();
    if (currentAppId) {
      set('app_id', currentAppId);
      ga4EventFields.app_id = currentAppId;
      gtag('set', 'user_properties', {'app_id': managingContextId});
    }

    let currentAppName = services.app.getCurrentApp()?.loc?.name;
    if (currentAppName) {
      set('app_name', currentAppName);
      ga4EventFields.app_name = currentAppName;
    }

    let experimentId = get(clientConfig, 'experiment.id');
    if (experimentId) {
      set('exp_id', experimentId);
      ga4EventFields.exp_id = experimentId;
    }

    let variantId = get(clientConfig, 'experiment.variant');
    if (variantId !== undefined) {
      set('exp_var', variantId);
      ga4EventFields.exp_var = variantId;
    }

    set('language', locale.toLowerCase());
    gtag('set', 'user_properties', {'language': locale.toLowerCase()});

    let managingContextId = get(clientConfig, 'managingContext.id');
    let contextId = get(clientConfig, 'context.id');
    if (managingContextId) {
      ga4EventFields.org_id = managingContextId;
      gtag('set', 'user_properties', {'org_id': managingContextId});

      if (contextId) {
        ga4EventFields.visiting_org_id = contextId;
        gtag('set', 'user_properties', {'visiting_org_id': contextId});
      }
      else {
        if (entityType === 'User') {
          ga4EventFields.visiting_org_id = 'USER_MANAGED_CONTEXT_WITH_NO_CONTEXT';
          gtag('set', 'user_properties', {'visiting_org_id': 'USER_MANAGED_CONTEXT_WITH_NO_CONTEXT'});
        }
        else if (entityType === 'Device') {
          ga4EventFields.visiting_org_id = 'DEVICE_MANAGED_CONTEXT_WITH_NO_CONTEXT';
          gtag('set', 'user_properties', {'visiting_org_id': 'DEVICE_MANAGED_CONTEXT_WITH_NO_CONTEXT'});
        }
        else {
          ga4EventFields.visiting_org_id = 'MANAGED_CONTEXT_WITH_NO_CONTEXT';
          gtag('set', 'user_properties', {'visiting_org_id': 'MANAGED_CONTEXT_WITH_NO_CONTEXT'});
        }
      }
    }
    else if (contextId) {
      ga4EventFields.org_id = contextId;
      gtag('set', 'user_properties', {'org_id': contextId});
    }
    else {
      if (entityType === 'User') {
        ga4EventFields.org_id = ORPHANED_USER_ORG;
        gtag('set', 'user_properties', {'org_id': ORPHANED_USER_ORG});
      }
      else if (entityType === 'Device') {
        ga4EventFields.org_id = 'DEVICE_WITH_NO_CONTEXT';
        gtag('set', 'user_properties', {'org_id': 'DEVICE_WITH_NO_CONTEXT'});
      }
    }
  }

  function sendNavigationTimingMetrics() {
    // Only track performance in supporting browsers.
    if (!(window.performance && window.performance.timing)) return;

    // If the window hasn't loaded, run this function after the `load` event.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', sendNavigationTimingMetrics);
      return;
    }

    const nt = performance.timing;
    const navStart = nt.navigationStart;

    const responseEnd = Math.round(nt.responseEnd - navStart);
    const domLoaded = Math.round(nt.domContentLoadedEventStart - navStart);
    const windowLoaded = Math.round(nt.loadEventStart - navStart);

    // In some edge cases browsers return very obviously incorrect NT values,
    // e.g. 0, negative, or future times. This validates values before sending.
    const allValuesAreValid = (...values) => {
      return values.every((value) => value > 0 && value < 1e6);
    };

    if (allValuesAreValid(responseEnd, domLoaded, windowLoaded)) {
      let fields = {
        category: services.app.getCurrentAppId(),
        action: 'navigation timing',
        non_interaction: true,
        [metrics.responseEndTime]: responseEnd,
        [metrics.domLoadTime]: domLoaded,
        [metrics.windowLoadTime]: windowLoaded,
        response_end_time: responseEnd,
        dom_load_time: domLoaded,
        window_load_time: windowLoaded,
      };
      eventTrack(fields.category, fields.action, 'navigation timing', null, fields);
    }
  }

  function sendApplicationLoadEvent() {
    eventTrack('printos', 'application load', services.app.getCurrentAppId());
  }

  function handleGA(command, type, category, action, label, value, fields) {
    if (command === 'send') {
      eventTrack(category, action, label, value, fields);
    }
  }

  function set(name, value) {
    gtag('set', name, value);
  }

  function sendLegacy(fieldsObject) {
    let eventName = fieldsObject?.event_name;
    if (!eventName) {
      eventName = fieldsObject?.action ? fieldsObject?.action : 'eventName';
    }

    gtag('event', eventName, fieldsObject);
  }

  function send(eventName, fieldsObject) {
    let clientConfig = services.session.getClientConfig();
    let myFields = {
      app_name: services.app.getCurrentApp()?.loc?.name,
      app_id: services.app.getCurrentAppId(),
      exp_id: get(clientConfig, 'experiment.id'),
      exp_var: get(clientConfig, 'experiment.variant'),
    };

    let metrics = {
      response_end_time: fieldsObject?.metric1,
      dom_load_time: fieldsObject?.metric2,
      window_load_time: fieldsObject?.metric3,
      ttfb: fieldsObject?.metric4,
      stalled: fieldsObject?.metric5,
      download: fieldsObject?.metric6,
      start_duration: fieldsObject?.metric7,
      end_duration: fieldsObject?.metric8,
      cached_resources: fieldsObject?.metric9,
    };

    let eventObject = {
      ...myFields,
      ...metrics,
      ...ga4EventFields,
      ...fieldsObject,
    };

    gtag('event', eventName, eventObject);
  }

  function sendToTag(eventName, fieldsObject, propertyId) {
    gtag('event', eventName, {
      ...fieldsObject,
      'send_to': propertyId,
    });
  }

  function gazelleSend(eventName, fieldsObj) {
    let eventCategory = fieldsObj.category;
    let eventAction = eventName;
    let eventLabel = fieldsObj.label;
    let eventValue = fieldsObj.value;
    eventTrack(eventCategory, eventAction, eventLabel, eventValue, fieldsObj);
  }

  function eventTrack(category, action, label, value, fields) {
    let eventName = fields?.event_name;
    if (!eventName) {
      eventName = action ? action : 'eventName';
    }

    let myFields = {
      category: category,
      eventCategory: category,
      event_category: category,
      action: action,
      eventAction: action,
      event_action: action,
      label: label,
      eventLabel: label,
      event_label: label,
      value: value,
      eventValue: value,
      event_value: value,
    };

    let eventObject = {
      ...myFields,
      ...fields,
    };

    send(eventName, eventObject);
  }

  function exceptionTrack(description, fatal = false) {
    let prefixedDescription = '[printos-blue] ' + description;

    // console.warn(prefixedDescription); // eslint-disable-line no-console
    if (process.env.NODE_ENV !== 'development') {
      let clientConfig = services.session.getClientConfig();

      gtag('event', 'exception', {
        exDescription: prefixedDescription,
        exFatal: fatal,
        ...ga4EventFields
      });
    }
  }

  function gazellePageTrack(id, page) {
    // Gazelle page tracking will turn off automatic history tracking (for legacy consistency)
    // Gazelle page tracking is ignored if manual page tracking is enabled
    if (manualChanges) {
      // Ignore if we are getting manual page changes
      return;
    }

    gazelleChanges = true;
    // If we get something through this channel we'll turn off our own trigger for legacy consistency
    if (historyChanges) {
      // console.log('GA4 Page tracking >> Turning off Auto Page Tracking due to Gazelle', id, page);
      historyChanges = false;
    }

    //console.log('GA4 Page tracking >> Gazelle', id, page);
    if (!initialized) {
      firstPage = {
        id: id,
        page: page,
        source: 'Gazelle',
      };
      return;
    }

    if (gazelleChanges) {
      _pageTrack(id, page, 'Gazelle');
    }
  }

  function pageTrack(id, page, customFields = {}) {
    // Manual page tracking will turn off automatic history tracking
    // Manual page tracking will turn off Gazelle page tracking
    manualChanges = true;
    if (gazelleChanges) {
      // console.log('GA4 Page tracking >> Turning off Gazelle Tracking due to Manual', id, page);
      gazelleChanges = false;
    }

    if (historyChanges) {
      // console.log('GA4 Page tracking >> Turning off Auto Page Tracking due to Manual', id, page);
      historyChanges = false;
    }

    //console.log('GA4 Page tracking >> Manual Page Track', id, page);
    if (!initialized) {
      firstPage = {
        id: id,
        page: page,
        source: 'Manual',
        customFields: customFields,
      };
      return;
    }

    _pageTrack(id, page, 'Manual', customFields);
  }

  function _pageTrack(id, page, source, customFields = {}) {
    //console.log(`GA4 Page tracking >> Tracking Page >> label:${id} page:${page} (${source})`);
    sendWhenAvailable(id, page, customFields);
  }

  function getUTMParams() {
    let hashSplit = window.location.href.split('#');
    let hashStr = null;
    if (hashSplit.length > 1) {
      if (hashSplit[1].indexOf('?') > -1) {
        hashStr = '?' + hashSplit[1].split('?')[1];
      }
    }
    const hashParams = new URLSearchParams(hashStr);
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_id: urlParams.get('utm_id') ? urlParams.get('utm_id') : hashParams.get('utm_id'),
      utm_source: urlParams.get('utm_source') ? urlParams.get('utm_source') : hashParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium') ? urlParams.get('utm_medium') : hashParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign') ? urlParams.get('utm_campaign') : hashParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term') ? urlParams.get('utm_term') : hashParams.get('utm_term'),
      utm_content: urlParams.get('utm_content') ? urlParams.get('utm_content') : hashParams.get('utm_content'),
      utm_source_platform: urlParams.get('utm_source_platform') ? urlParams.get('utm_source_platform') : hashParams.get('utm_source_platform'),
      utm_creative_format: urlParams.get('utm_creative_format') ? urlParams.get('utm_creative_format') : hashParams.get('utm_creative_format'),
      utm_marketing_tactic: urlParams.get('utm_marketing_tactic') ? urlParams.get('utm_marketing_tactic') : hashParams.get('utm_marketing_tactic'),
    };
  }

  function sendWhenAvailable(id, page, customFields = {}) {
    if (!gtag || !initialized) {
      setTimeout(() => {sendWhenAvailable(id, page, customFields);}, 1000);
      return;
    }

    set('page', page);
    const utmParams = getUTMParams();
    //services.window.ga('send', 'pageview', page);
    eventTrack('event', 'page_view', id ? id : page, page, {
      event_name: 'page_view',
      send_to: analytics4Key,
      page: page,
      page_path: window.location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      // custom_map: {
      //   dimension1: 'utm_source',
      //   dimension2: 'utm_medium',
      //   dimension3: 'utm_campaign',
      //   dimension4: 'utm_term',
      //   dimension5: 'utm_content',
      //   dimension6: 'utm_id',
      //   dimension7: 'utm_source_platform',
      //   dimension8: 'utm_creative_format',
      //   dimension9: 'utm_marketing_tactic',
      // },
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
      utm_content: utmParams.utm_content,
      utm_id: utmParams.utm_id,
      utm_source_platform: utmParams.utm_source_platform,
      utm_creative_format: utmParams.utm_creative_format,
      utm_marketing_tactic: utmParams.utm_marketing_tactic,
      ...ga4EventFields,
      ...customFields,
    });
  }
  function registerForVisibilityEvents() {
    // Listen for tab visibility changes
    services.document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for window close or reload
    services.window.addEventListener('beforeunload', handleWindowClose);
  }

  function handleVisibilityChange() {
    if (services.document.hidden) {
      pageVisibilityEnd('tab changed');
    } else {
      pageVisibilityStart();
    }
  }

  function handleWindowClose() {
    pageVisibilityEnd('window closed');
  }

  function handlePageView(newPage) {
    if (!visibilityPage) {
      // First page view
      pageVisibilityStart(newPage);
    } else {
      pageVisibilityEnd('view changed');
      // Ending one page view, starting another
      pageVisibilityStart(newPage);
    }
  }

  function pageVisibilityStart(newPage) {
    //console.log('pageVisibilityStart', newPage);
    if (newPage) {
      visibilityPage = newPage;
    }
    // Ensure no other start mark exists
    services.window.performance.clearMarks(PAGE_VISIBILITY_START_LABEL);
    services.window.performance.mark(PAGE_VISIBILITY_START_LABEL);
  }

  function pageVisibilityEnd(reason) {
    let startMark = services.window.performance.getEntriesByName(PAGE_VISIBILITY_START_LABEL, 'mark');
    if (startMark.length === 0) {
      // No start mark - this can happen on reload because both beforeunload and visibilitychange events fire.
      return;
    }

    services.window.performance.mark(PAGE_VISIBILITY_END_LABEL);
    services.window.performance.measure(PAGE_VISIBILITY_LABEL, PAGE_VISIBILITY_START_LABEL, PAGE_VISIBILITY_END_LABEL);
    let measures = services.window.performance.getEntriesByName(PAGE_VISIBILITY_LABEL, 'measure');
    if (!measures) {
      exceptionTrack(`Failed to track page visibility with reason "${reason}"`);
      return;
    }

    let measure = measures[0];
    // Report visibility in seconds.
    let visibilitySec = services.window.Math.round(measure.duration / 1000);
    // Track visibility with last page
    eventTrack('page visibility', reason, undefined, visibilitySec, {
      non_interaction: true,
      page: visibilityPage,
    });
    services.window.performance.clearMarks(PAGE_VISIBILITY_START_LABEL);
    services.window.performance.clearMarks(PAGE_VISIBILITY_END_LABEL);
    services.window.performance.clearMeasures(PAGE_VISIBILITY_LABEL);
  }
}
